/// <reference types="user-agent-data-types" />

import { useEffect, useState } from 'react';
import { UAParser } from 'ua-parser-js';
import { useMediaQuery } from 'usehooks-ts';
import { useLayoutStore } from '@/stores/layout-status';
import { BUILD_ENV } from '@/utils/constants';
import { getStandaloneMode } from '@/utils/get-standalone-mode';

export type StandaloneMode = 'standalone' | 'embedded' | 'web';

interface ClientHints {
  osType: 'desktop' | 'mobile';
  osName: string;
  osVersion: string;
  browserName: string;
  browserVersion: string;
}

interface UserHints {
  browser: string;
  hints: ClientHints;
}

interface UserPlatform {
  mode: StandaloneMode;
  isIOS: boolean;
  isAndroid: boolean;
  isDesktop: boolean;
  isStandalone: boolean;
  isEmbedded: boolean;
}

export default function useUserPlatform(): UserPlatform {
  const isDesktop = useIsDesktop();

  const isStandalone = getStandaloneMode();
  const isEmbedded = BUILD_ENV === 'embedded';

  return {
    mode: isEmbedded ? 'embedded' : isStandalone ? 'standalone' : 'web',
    isStandalone,
    isEmbedded,
    isDesktop,
    ...getUserPlatform(),
  };
}

export function useUserHints() {
  const [state, setState] = useState<UserHints>(() => ({
    browser: 'Unknown',
    hints: {
      osType: 'desktop',
      osName: '',
      osVersion: '',
      browserName: '',
      browserVersion: '',
    },
  }));

  useEffect(() => {
    (async () => {
      const hints = await getBrowserMetadata();
      setState((s) => ({
        ...s,
        browser: [hints.osName, hints.osVersion, hints.browserName, hints.browserVersion].join(' '),
        hints,
      }));
    })();
  }, []);

  return state;
}

export async function getUserAgent(): Promise<UserHints> {
  const hints = await getBrowserMetadata();

  return {
    browser: [hints.osName, hints.osVersion, hints.browserName, hints.browserVersion].join(' '),
    hints,
  };
}

export function getUserPlatform(): Readonly<Pick<UserPlatform, 'isIOS' | 'isAndroid'>> {
  const ua = UAParser(navigator.userAgent);

  return {
    isIOS: ua?.os.name === 'iOS' || (ua?.os.name === 'Mac OS' && navigator.maxTouchPoints >= 1),
    isAndroid: ua?.os.name === 'Android',
  };
}

export async function getBrowserMetadata(): Promise<ClientHints> {
  const result: ClientHints = {
    osType: 'desktop',
    osName: '',
    osVersion: '',
    browserName: '',
    browserVersion: '',
  };

  const ua = UAParser(navigator.userAgent);
  if (!ua) return result;

  const os = await ua.os.withClientHints();
  const browser = await ua.browser.withClientHints();

  result.osType = getOsTypeByName(os.name);
  result.osName = os.name ?? '';
  result.osVersion = os.version ?? '';
  result.browserName = browser.name ?? '';
  result.browserVersion = browser.version ?? '';

  return result;
}

function getOsTypeByName(osName: string | undefined): 'desktop' | 'mobile' {
  switch (osName) {
    case 'iOS':
    case 'Android':
      return 'mobile';
    default:
      return 'desktop';
  }
}

export const breakpoints = {
  desktop: 1024,
};

export const useIsDesktop = () => useLayoutStore((state) => state.isDesktop);

export const getIsDesktop = () => useLayoutStore.getState().isDesktop;

export const useScreenOrientation = () => (useMediaQuery('(orientation: portrait)') ? 'portrait' : 'landscape');

export const useDisplayMode = () => useLayoutStore((s) => (s.isDesktop ? 'desktop' : 'mobile'));
